import { EModalAssetsManagerView, ModalAssetsManager } from 'src/components/ModalAssetsManager';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  initialApplicationState,
  setModalHouseSummaryFormVariant,
  setModalHouseSummaryView,
} from 'src/redux/application/application.slice';
import {
  selectActiveConfigurationUuid,
  selectAssetsTreeRelations,
  selectAssetsUnderUuid,
  selectAssetsValues,
  selectCommunityAsset,
  selectConfigurationCenter,
  selectGridMarketAsset,
} from 'src/redux/configuration/configuration.selectors';
import {
  selectIsEmbed,
  selectModalHouseSummaryFormVariant,
  selectModalHouseSummaryView,
  selectOnboardingStep,
} from 'src/redux/application/application.selectors';

import { BaseIcon } from 'src/components/BaseIcon/BaseIcon';
import { ModalCommunitySummary } from 'src/components/ModalCommunitySummary';
import { Popup } from 'src/components/WorldMap/components/Popup';
import { TWorldMapSelectedCommunityAssetsProps } from './WorldMapSelectedCommunityAssets.types';
import WorldMapContext from 'src/contexts/WorldMapContext';
import { convertLngLat } from 'src/utils/worldMap/helpers';
import s from './WorldMapSelectedCommunityAssets.module.scss';
import { setSelectedAssetUuid } from 'src/redux/configuration/configuration.slice';
import { throttle } from 'lodash';
import { useAppDispatch } from 'src/redux/store';
import { useSelector } from 'react-redux';
import { selectActiveModals } from 'src/redux/modals/modals.selectors';
import { useConfigurationFlowState } from 'src/hooks/useConfigurationFlowState';

export const WorldMapSelectedCommunityAssets: React.FC<TWorldMapSelectedCommunityAssetsProps> = React.memo(
  ({
    selectedAssetUuid,
    disableInteraction,
    onAddNewCustomHouse,
    onSettingsDataSave,
    onAssetValuesSave,
    onAssetRemove,
    onCommunityRemove,
    onAssetDuplicate,
    onAddAssetUnderUuid,
    onGridMarketClick,
  }) => {
    const dispatch = useAppDispatch();
    const isEmbed = useSelector(selectIsEmbed);
    const assetsValues = useSelector(selectAssetsValues);
    const gridMarketAsset = useSelector(selectGridMarketAsset);
    const communityAsset = useSelector(selectCommunityAsset);
    const assetsUnderSelectedUuid = useSelector(selectAssetsUnderUuid(communityAsset?.uuid));
    const assetsTreeRelations = useSelector(selectAssetsTreeRelations);
    const configurationCenter = useSelector(selectConfigurationCenter);
    const activeConfigurationUuid = useSelector(selectActiveConfigurationUuid);
    const { mapService } = useContext(WorldMapContext);
    const [summaryModalPos, setSummaryModalPos] = useState({
      lng: configurationCenter?.lng,
      lat: configurationCenter?.lat,
    });
    const [gridModalPos, setGridModalPos] = useState({
      lng: configurationCenter?.lng,
      lat: configurationCenter?.lat,
    });
    const modalHouseSummaryView = useSelector(selectModalHouseSummaryView);
    const modalHouseSummaryFormVariant = useSelector(selectModalHouseSummaryFormVariant);
    const step = useSelector(selectOnboardingStep);
    const activeModals = useSelector(selectActiveModals);

    const showCommunityOnTheMap =
      communityAsset &&
      (assetsTreeRelations[communityAsset.uuid].length > 1 || activeConfigurationUuid);
    const showCommunityModal = Boolean(
      selectedAssetUuid && selectedAssetUuid === communityAsset?.uuid,
    );
    const showMarketModal = Boolean(
      selectedAssetUuid && selectedAssetUuid === gridMarketAsset?.uuid,
    );

    function handleGridMarketClick() {
      const uuid = gridMarketAsset?.uuid;

      if (!uuid) return;

      onGridMarketClick({
        lngLat: { lng: 0, lat: 0 }, // This location doesn't matter, it's passed to satisfy TypeScript
        target: {
          overedObject: {
            _asset: {
              uuid,
            },
          },
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const calculateCommunitySummaryModalPos = useCallback(
      throttle(() => {
        const communityRing = mapService?.threeboxController.getCommunityRingObject();

        if (communityRing && configurationCenter) {
          const summaryAdjustedPos = communityRing.calculateAdjustedPosition(
            [configurationCenter.lng, configurationCenter.lat],
            { x: -2.1, y: 0, z: 0 },
          );
          const gridAdjustedPos = communityRing.calculateAdjustedPosition(
            [configurationCenter.lng, configurationCenter.lat],
            { x: -2.15, y: -18, z: 0 },
          );

          setSummaryModalPos({
            lat: summaryAdjustedPos[1],
            lng: summaryAdjustedPos[0],
          });

          setGridModalPos({
            lat: gridAdjustedPos[1],
            lng: gridAdjustedPos[0],
          });
        }
      }, 100),
      [configurationCenter, mapService?.threeboxController, summaryModalPos],
    );

    useEffect(() => {
      mapService?.map.on('load', calculateCommunitySummaryModalPos);
      mapService?.map.on('move', calculateCommunitySummaryModalPos);
      return () => {
        mapService?.map.off('load', calculateCommunitySummaryModalPos);
        mapService?.map.off('move', calculateCommunitySummaryModalPos);
      };
    }, [calculateCommunitySummaryModalPos, mapService]);

    return (
      <>
        {showCommunityOnTheMap && configurationCenter && communityAsset && (
          <Popup
            lat={summaryModalPos.lat}
            lng={summaryModalPos.lng}
            className={communityAsset.type === 'Area' ? s.rootMarker : s.marker}
            higherZIndex={showCommunityModal}>
            <div>
              {activeModals.includes('modal-onboarding') && (step == 1 || step == 2) ? null : (
                <button
                  type="button"
                  className={s.createCommunityMarket}
                  onClick={() => dispatch(setSelectedAssetUuid(communityAsset.uuid))}>
                  <BaseIcon icon="community" size={14} />
                </button>
              )}

              <ModalCommunitySummary
                hostAssetUuid={communityAsset.uuid}
                onAddAssetUnderUuid={onAddAssetUnderUuid}
                onAssetDuplicate={onAssetDuplicate}
                onAssetRemove={onAssetRemove}
                onAssetValuesSave={onAssetValuesSave}
                onCommunityRemove={onCommunityRemove}
                onSettingsDataSave={onSettingsDataSave}
                onAddNewCustomHouse={onAddNewCustomHouse}
              />
            </div>
          </Popup>
        )}

        {gridMarketAsset && gridModalPos && !isEmbed && (
          <Popup lat={gridModalPos.lat} lng={gridModalPos.lng} higherZIndex={showMarketModal}>
            <button type="button" className={s.gridMarketBullet} onClick={handleGridMarketClick}>
              <BaseIcon icon="grid-market" size={11} />
            </button>

            {showMarketModal && (
              <ModalAssetsManager
                summaryIcon="market"
                hostAssetUuid={gridMarketAsset.uuid}
                view={EModalAssetsManagerView.Form}
                viewOnDestroy={EModalAssetsManagerView.Summary}
                onViewChange={(v) => dispatch(setModalHouseSummaryView(v))}
                disallowReturnToSummary={() => dispatch(setSelectedAssetUuid(undefined))}
                onAssetValuesSave={onAssetValuesSave}
              />
            )}
          </Popup>
        )}

        {assetsUnderSelectedUuid.map((asset) => {
          const { geoTagLocation } = assetsValues[asset.uuid];

          if (!geoTagLocation) return null;
          const lngLat = convertLngLat(geoTagLocation);
          const showThisAssetModal = selectedAssetUuid === asset.uuid;
          const isSingleDevice = asset.type !== 'Area';

          return (
            <Popup
              key={asset.uuid}
              lat={lngLat.lat}
              lng={lngLat.lng}
              className={s.marker}
              higherZIndex={showThisAssetModal}>
              {/* <button
                type="button"
                className={s.housePinBtn}
                disabled={disableInteraction}
                onClick={() => dispatch(setSelectedAssetUuid(asset.uuid))}>
                <BaseIcon icon="pin" size={30} />
              </button> */}

              {showThisAssetModal && (
                <ModalAssetsManager
                  summaryIcon="market"
                  hostAssetUuid={asset.uuid}
                  view={!isSingleDevice ? modalHouseSummaryView : EModalAssetsManagerView.Form}
                  onViewChange={(v) => dispatch(setModalHouseSummaryView(v))}
                  viewOnDestroy={initialApplicationState.modalHouseSummaryView}
                  formVariant={modalHouseSummaryFormVariant}
                  onFormVariantChange={(v) => dispatch(setModalHouseSummaryFormVariant(v))}
                  disallowReturnToSummary={
                    isSingleDevice ? () => dispatch(setSelectedAssetUuid(undefined)) : undefined
                  }
                  onAddAssetUnderUuid={!isSingleDevice ? onAddAssetUnderUuid : undefined}
                  onAssetDuplicate={onAssetDuplicate}
                  onAssetRemove={onAssetRemove}
                  onAssetValuesSave={onAssetValuesSave}
                  onAddNewCustomHouse={onAddNewCustomHouse}
                />
              )}
            </Popup>
          );
        })}
      </>
    );
  },
);
